import './DataDictionaryTable.scss';
import './SharedDataDictionaryTable.scss';

import { PaginatedTable } from '@ui/tables';
import { PaginationDirection } from '@sonar-software/sonar-ts-types';
import { SearchInput } from '@ui/general';
import { SharedDataDictionaryQueryParams } from './SharedDataDictionary';
import { SonarText } from '@ui/text';
import { SortByFilterQueryParams } from '@periscopes/general/filters';
import { SortedBySelect } from '@ui/table-modifier/SortedBy';
import { Space } from 'antd';
import { UpdateQueryParams } from '@hooks';

import { COPY_SPECIFIC, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import {
	DataDictionaryEntry,
	DataDictionarySchema,
	GetSharedDataDictionaryResponse
} from '@sonar-software/sonar-core';
import {
	getAppliedOrdering,
	parseMetadataFromResponse,
	translateFilterAndOrderingToQueryParams,
	translateOrderQueryParamsToDynamicQueryLanguage
} from '@ui/table-modifier/@arch/lib';
import React, { useCallback, useMemo } from 'react';

export interface SharedDataDictionaryTableProps {
	loading: boolean;
	resource: GetSharedDataDictionaryResponse | null | undefined;
	updateQuery: UpdateQueryParams<SharedDataDictionaryQueryParams>;
}

const rowKey = (entry: DataDictionaryEntry) => entry.sonarObjectId;

export const SharedDataDictionaryTable: React.FC<SharedDataDictionaryTableProps> = ({
	loading,
	resource,
	updateQuery
}) => {
	const { schema, sortOptions } = useMemo(() => {
		const results = parseMetadataFromResponse(resource?.schema, () => true, atob(resource?.appliedColumns ?? ''));

		return {
			schema: results.schema,
			sortOptions: results.sortOptions
		};
	}, [ resource ]);

	const sortQueryParams = useMemo(
		() =>
			translateFilterAndOrderingToQueryParams(
				[],
				sortOptions,
				'',
				getAppliedOrdering(resource?.appliedOrderingAndFilters ?? '')
			),
		[ resource?.appliedOrderingAndFilters, sortOptions ]
	);

	const header = resource ? `${resource.organizationName} - ${resource.viewName}` : '';

	const handleSortUpdate = useCallback(
		(update: Partial<SortByFilterQueryParams>) => {
			updateQuery({
				key: '',
				ordering: translateOrderQueryParamsToDynamicQueryLanguage(update),
				paginationDirection: PaginationDirection.Forward
			});
		},
		[ updateQuery ]
	);

	const handleFilterByName = useCallback(
		(name: string) => {
			updateQuery({
				filterByName: name
			});
		},
		[ updateQuery ]
	);

	return (
		<>
			<Space className='shared-view-header'>
				<SonarText
					size={ 16 }
					weight='semibold'>
					{ header }
				</SonarText>
				{ resource &&
					<SortedBySelect
						defaultValue={ sortQueryParams.sortDefault || sortOptions[ 0 ] }
						onChange={ handleSortUpdate }
						queryParams={ sortQueryParams }
						sortOptions={ sortOptions } /> }
			</Space>
			<SearchInput
				className='filter-by-name'
				id='filterByName'
				onChange={ handleFilterByName }
				placeholder={ PLACEHOLDERS_COPY.FILTER_BY(COPY_SPECIFIC.NAME) } />
			<PaginatedTable<DataDictionaryEntry, DataDictionarySchema>
				className='data-dictionary-table shared-data-dictionary-table no-click'
				columns={ schema }
				defaultPageSize={ 10 }
				loading={ loading }
				onChange={ updateQuery }
				resource={ resource }
				rowKey={ rowKey }
				scroll={ {
					scrollToFirstRowOnChange: true,
					x: resource?.results.length ? 'max-content' : 4000
				} } />
		</>
	);
};
